import { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTheme } from '../context/ThemeContext'
import { apiConfig } from '../auth/authConfig'
import GanttChart from '../components/GanttChart'
import DraggableQueue from '../components/DraggableQueue'
import ScheduleModal from '../components/ScheduleModal'

function WorkoverSchedule() {
    const { theme } = useTheme()
    const navigate = useNavigate()
    const isDark = theme === 'dark'

    const [rigs, setRigs] = useState([])
    const [workovers, setWorkovers] = useState([])
    const [queue, setQueue] = useState([])
    const [allWells, setAllWells] = useState([])
    const [history, setHistory] = useState([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)

    // History timeline state
    const [historySearch, setHistorySearch] = useState('')
    const [historyYear, setHistoryYear] = useState('')

    // Modal state
    const [modalOpen, setModalOpen] = useState(false)
    const [modalMode, setModalMode] = useState('create') // 'create' | 'drop' | 'edit'
    const [modalData, setModalData] = useState({})

    useEffect(() => { fetchData() }, [])

    const fetchData = async () => {
        setLoading(true)
        setError(null)
        try {
            const res = await fetch(`${apiConfig.baseUrl}/workovers/schedule`)
            if (!res.ok) throw new Error('Failed to fetch schedule data')
            const json = await res.json()
            setRigs(json.rigs || [])
            setWorkovers(json.workovers || [])
            setQueue(json.queue || [])
            setAllWells(json.allWells || [])
            setHistory(json.history || [])
        } catch (err) {
            setError(err.message)
        } finally {
            setLoading(false)
        }
    }

    const handleReorder = async (items) => {
        const res = await fetch(`${apiConfig.baseUrl}/workovers/rig-queue/reorder`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items })
        })
        if (!res.ok) throw new Error('Failed to save queue order')
        setQueue(prev => {
            const idMap = Object.fromEntries(items.map(i => [i.id, i.position]))
            return prev
                .map(q => idMap[q.id] != null ? { ...q, position: idMap[q.id] } : q)
                .sort((a, b) => a.position - b.position)
        })
    }

    // Queue item dropped onto a rig row
    const handleDropOnRig = (queueItem, rigName) => {
        setModalMode('drop')
        setModalData({ ...queueItem, rig: rigName })
        setModalOpen(true)
    }

    // Click on an existing bar
    const handleBarClick = (workover) => {
        setModalMode('edit')
        setModalData(workover)
        setModalOpen(true)
    }

    // Drag-resize a bar's right edge
    const handleBarResize = async (workover, newEndDate) => {
        try {
            const res = await fetch(`${apiConfig.baseUrl}/workovers/schedule/${workover.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ endDate: newEndDate })
            })
            if (!res.ok) throw new Error('Failed to update')
            await fetchData()
        } catch (err) {
            console.error('Resize failed:', err)
            await fetchData() // revert visual
        }
    }

    // "+" button
    const handleAddNew = () => {
        setModalMode('create')
        setModalData({})
        setModalOpen(true)
    }

    // Modal save handler
    const handleModalSave = async (formData) => {
        const url = formData.mode === 'edit'
            ? `${apiConfig.baseUrl}/workovers/schedule/${formData.id}`
            : `${apiConfig.baseUrl}/workovers/schedule`

        const method = formData.mode === 'edit' ? 'PUT' : 'POST'

        const res = await fetch(url, {
            method,
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                well: formData.well,
                rig: formData.rig,
                type: formData.workType,
                reason: formData.reason,
                estCost: formData.estCost,
                defBopd: formData.defBopd,
                startDate: formData.startDate,
                endDate: formData.endDate,
                status: formData.status
            })
        })

        if (!res.ok) {
            const err = await res.json().catch(() => ({}))
            throw new Error(err.details || err.error || 'Failed to save workover')
        }

        await fetchData()
    }

    // Modal delete handler
    const handleModalDelete = async (id) => {
        const res = await fetch(`${apiConfig.baseUrl}/workovers/schedule/${id}`, { method: 'DELETE' })
        if (!res.ok) {
            const err = await res.json().catch(() => ({}))
            throw new Error(err.details || err.error || 'Failed to delete workover')
        }
        await fetchData()
    }

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-aviator-600"></div>
            </div>
        )
    }

    if (error && !rigs.length) {
        return (
            <div className="bg-red-900/20 border border-red-800 rounded-lg p-4 text-red-400">
                <p className="font-semibold">Error loading schedule</p>
                <p className="text-sm mt-1">{error}</p>
                <button onClick={fetchData}
                    className="mt-3 text-sm bg-red-800 hover:bg-red-700 text-white px-3 py-1 rounded transition">
                    Retry
                </button>
            </div>
        )
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className={`flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 border-b-2 ${isDark ? 'border-blue-800' : 'border-blue-200'} pb-4`}>
                <div>
                    <h1 className={`text-xl sm:text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'} flex items-center gap-2 sm:gap-3`}>
                        <span className="inline-block w-1 h-6 sm:h-8 bg-blue-500 flex-shrink-0"></span>
                        Workover Schedule
                    </h1>
                    <p className={`${isDark ? 'text-blue-300' : 'text-blue-700'} text-xs sm:text-sm mt-1 ml-3 sm:ml-4`}>
                        Rig timeline and queue management
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <button onClick={handleAddNew}
                        className="px-3 py-1.5 text-sm rounded-lg transition bg-emerald-600 text-white hover:bg-emerald-700 font-medium">
                        + Schedule
                    </button>
                    <button onClick={() => navigate('/workovers')}
                        className={`px-3 py-1.5 text-sm rounded-lg transition ${
                            isDark
                                ? 'bg-slate-900 text-gray-300 hover:bg-slate-800 border border-slate-800'
                                : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                        }`}>
                        &larr; Overview
                    </button>
                    <button onClick={fetchData}
                        className={`px-2.5 py-1.5 text-sm rounded-lg transition ${
                            isDark
                                ? 'bg-slate-900 text-gray-300 hover:bg-slate-800 border border-slate-800'
                                : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                        }`} title="Refresh">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                    </button>
                </div>
            </div>

            {/* Workover History Timeline */}
            {history.length > 0 && <WorkoverHistoryTimeline
                history={history}
                isDark={isDark}
                search={historySearch}
                setSearch={setHistorySearch}
                year={historyYear}
                setYear={setHistoryYear}
                navigate={navigate}
            />}

            {/* Draggable Queue */}
            <div className="chart-container">
                <div className="flex justify-between items-center mb-4">
                    <h2 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        Wells Down ({queue.length})
                    </h2>
                </div>
                <DraggableQueue queue={queue} onReorder={handleReorder} onAddNew={handleAddNew} />
            </div>

            {/* Schedule Modal */}
            <ScheduleModal
                isOpen={modalOpen}
                onClose={() => setModalOpen(false)}
                onSave={handleModalSave}
                onDelete={handleModalDelete}
                mode={modalMode}
                initialData={modalData}
                rigs={rigs}
                allWells={allWells}
            />
        </div>
    )
}

const HIST_RANGES = [
    { label: '1Y', days: 365 },
    { label: '2Y', days: 730 },
    { label: '5Y', days: 1825 },
    { label: 'All', days: 0 }
]

function WorkoverHistoryTimeline({ history, isDark, search, setSearch, year, setYear, navigate }) {
    const containerRef = useRef(null)
    const [activeRange, setActiveRange] = useState('2Y')
    const [tooltip, setTooltip] = useState(null)

    const years = useMemo(() => {
        const set = new Set()
        history.forEach(h => { if (h.startDate) set.add(h.startDate.slice(0, 4)) })
        return Array.from(set).sort().reverse()
    }, [history])

    // Filter by search + year
    const filtered = useMemo(() => {
        let items = history
        if (year) items = items.filter(h => h.startDate?.startsWith(year))
        if (search) {
            const q = search.toLowerCase()
            items = items.filter(h =>
                (h.well || '').toLowerCase().includes(q) ||
                (h.workType || '').toLowerCase().includes(q) ||
                (h.reason || '').toLowerCase().includes(q)
            )
        }
        return items
    }, [history, year, search])

    // Date boundaries
    const { startDate, endDate, totalDays } = useMemo(() => {
        const now = new Date()
        let start, end
        if (activeRange === 'All') {
            const dates = filtered.map(w => new Date(w.startDate)).filter(d => !isNaN(d))
            start = dates.length > 0 ? new Date(Math.min(...dates)) : new Date(now.getFullYear() - 2, 0, 1)
            end = new Date(now.getFullYear(), now.getMonth() + 2, 0)
        } else {
            const range = HIST_RANGES.find(r => r.label === activeRange)
            start = new Date(now)
            start.setDate(start.getDate() - range.days)
            end = new Date(now)
            end.setDate(end.getDate() + 30)
        }
        return { startDate: start, endDate: end, totalDays: Math.ceil((end - start) / (1000 * 60 * 60 * 24)) }
    }, [activeRange, filtered])

    // Month markers
    const monthMarkers = useMemo(() => {
        const markers = []
        const d = new Date(startDate.getFullYear(), startDate.getMonth(), 1)
        while (d <= endDate) {
            const pct = ((d - startDate) / (1000 * 60 * 60 * 24) / totalDays) * 100
            if (pct >= 0 && pct <= 100) markers.push({ label: d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' }), pct })
            d.setMonth(d.getMonth() + 1)
        }
        return markers
    }, [startDate, endDate, totalDays])

    const todayPct = useMemo(() => ((new Date() - startDate) / (1000 * 60 * 60 * 24) / totalDays) * 100, [startDate, totalDays])

    // Compute bar position
    const computeBar = useCallback((w) => {
        if (!w.startDate) return null
        const ws = new Date(w.startDate + 'T12:00')
        const we = w.endDate ? new Date(w.endDate + 'T12:00') : new Date()
        const leftPct = ((ws - startDate) / (1000 * 60 * 60 * 24) / totalDays) * 100
        const widthPct = (Math.max((we - ws) / (1000 * 60 * 60 * 24), 1) / totalDays) * 100
        if (leftPct + widthPct < 0 || leftPct > 100) return null
        return { ...w, leftPct: Math.max(leftPct, 0), widthPct: Math.min(widthPct, 100 - Math.max(leftPct, 0)) }
    }, [startDate, totalDays])

    // Group by well, compute bars, sort by most recent workover
    const wellRows = useMemo(() => {
        const wellMap = new Map()
        filtered.forEach(w => {
            const key = w.well || 'Unknown'
            if (!wellMap.has(key)) wellMap.set(key, [])
            wellMap.get(key).push(w)
        })
        return Array.from(wellMap.entries())
            .map(([wellName, wos]) => ({
                wellName,
                bars: wos.map(computeBar).filter(Boolean),
                latestStart: Math.max(...wos.map(w => new Date(w.startDate || 0).getTime()))
            }))
            .filter(r => r.bars.length > 0)
            .sort((a, b) => b.latestStart - a.latestStart)
    }, [filtered, computeBar])

    const formatDate = (d) => d ? new Date(d + 'T12:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'Ongoing'
    const formatCost = (v) => v != null ? `$${Math.round(v).toLocaleString()}` : ''

    const getBarColor = (status) => {
        const s = (status || '').toLowerCase()
        if (s === 'active') return 'bg-blue-500'
        return isDark ? 'bg-emerald-600' : 'bg-emerald-500'
    }

    const handleBarHover = (e, wo) => {
        const rect = containerRef.current?.getBoundingClientRect()
        if (!rect) return
        setTooltip({ x: e.clientX - rect.left, y: e.clientY - rect.top - 10, wo })
    }

    return (
        <div className="chart-container" ref={containerRef}>
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4">
                <h2 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    Workover History ({filtered.length} jobs, {wellRows.length} wells)
                </h2>
                <div className="flex items-center gap-2">
                    <input
                        type="text"
                        placeholder="Search well, type..."
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        className={`px-2.5 py-1.5 text-xs rounded-lg border ${
                            isDark ? 'bg-slate-800 border-slate-700 text-gray-200 placeholder-gray-500' : 'bg-white border-gray-300 text-gray-800 placeholder-gray-400'
                        } w-48`}
                    />
                    <select
                        value={year}
                        onChange={e => setYear(e.target.value)}
                        className={`px-2.5 py-1.5 text-xs rounded-lg border ${
                            isDark ? 'bg-slate-800 border-slate-700 text-gray-200' : 'bg-white border-gray-300 text-gray-800'
                        }`}
                    >
                        <option value="">All Years</option>
                        {years.map(y => <option key={y} value={y}>{y}</option>)}
                    </select>
                </div>
            </div>

            {/* Range buttons + legend */}
            <div className="flex items-center gap-1.5 mb-3">
                {HIST_RANGES.map(r => (
                    <button
                        key={r.label}
                        onClick={() => setActiveRange(r.label)}
                        className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                            activeRange === r.label
                                ? 'bg-blue-600 text-white'
                                : isDark ? 'bg-slate-700 text-gray-300 hover:bg-slate-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }`}
                    >{r.label}</button>
                ))}
                <div className="flex items-center gap-3 ml-4">
                    <span className="flex items-center gap-1.5 text-[11px]">
                        <span className={`w-3 h-2 rounded-sm ${isDark ? 'bg-emerald-600' : 'bg-emerald-500'}`}></span>
                        <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>Completed</span>
                    </span>
                    <span className="flex items-center gap-1.5 text-[11px]">
                        <span className="w-3 h-2 rounded-sm bg-blue-500"></span>
                        <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>Active</span>
                    </span>
                </div>
            </div>

            {/* Timeline header */}
            <div className={`relative h-6 border-b ${isDark ? 'border-slate-700' : 'border-gray-300'} ml-40`}>
                {monthMarkers.map((m, i) => (
                    <div key={i} className="absolute top-0 h-full flex items-end" style={{ left: `${m.pct}%` }}>
                        <span className={`text-[10px] font-medium ${isDark ? 'text-gray-500' : 'text-gray-400'} whitespace-nowrap -translate-x-1/2`}>{m.label}</span>
                    </div>
                ))}
            </div>

            {/* Scrollable well rows */}
            <div className="relative max-h-[500px] overflow-y-auto">
                {wellRows.map(({ wellName, bars }) => (
                    <div key={wellName} className={`flex border-b min-h-[36px] ${isDark ? 'border-slate-800' : 'border-gray-200'}`}>
                        <div className={`w-40 flex-shrink-0 flex items-center px-2 py-1 ${isDark ? 'bg-slate-900/50' : 'bg-gray-50'}`}>
                            <p className={`text-[11px] font-medium truncate ${isDark ? 'text-gray-300' : 'text-gray-700'}`} title={wellName}>
                                {wellName}
                            </p>
                        </div>
                        <div className="flex-1 relative py-1">
                            {monthMarkers.map((m, i) => (
                                <div key={i} className={`absolute top-0 bottom-0 w-px ${isDark ? 'bg-slate-800/50' : 'bg-gray-100'}`} style={{ left: `${m.pct}%` }} />
                            ))}
                            {todayPct >= 0 && todayPct <= 100 && (
                                <div className="absolute top-0 bottom-0 w-px bg-red-500/40 z-10" style={{ left: `${todayPct}%` }} />
                            )}
                            {bars.map(bar => (
                                <div
                                    key={bar.id}
                                    className={`absolute top-1 h-6 rounded cursor-pointer transition-opacity hover:opacity-80 flex items-center px-1 overflow-hidden ${getBarColor(bar.status)}`}
                                    style={{ left: `${bar.leftPct}%`, width: `${Math.max(bar.widthPct, 0.3)}%` }}
                                    onClick={() => navigate(`/workovers/detail/${bar.id}`)}
                                    onMouseEnter={(e) => handleBarHover(e, bar)}
                                    onMouseMove={(e) => handleBarHover(e, bar)}
                                    onMouseLeave={() => setTooltip(null)}
                                >
                                    {bar.widthPct > 4 && (
                                        <span className="text-[9px] text-white font-medium truncate pointer-events-none">
                                            {bar.workType || ''}
                                        </span>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
                {wellRows.length === 0 && (
                    <div className={`py-8 text-center text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                        No workovers match the filters
                    </div>
                )}
            </div>

            {/* Tooltip */}
            {tooltip && (
                <div
                    className={`absolute z-30 px-3 py-2 rounded-lg shadow-lg text-xs pointer-events-none ${
                        isDark ? 'bg-slate-800 border border-slate-600 text-gray-200' : 'bg-white border border-gray-300 text-gray-800'
                    }`}
                    style={{
                        left: Math.min(tooltip.x, (containerRef.current?.offsetWidth || 400) - 220),
                        top: tooltip.y - 80
                    }}
                >
                    <p className="font-semibold">{tooltip.wo.well}</p>
                    <p className={isDark ? 'text-gray-400' : 'text-gray-500'}>{tooltip.wo.workType}{tooltip.wo.reason ? ` — ${tooltip.wo.reason}` : ''}</p>
                    <p className={isDark ? 'text-gray-400' : 'text-gray-500'}>
                        {formatDate(tooltip.wo.startDate)} — {formatDate(tooltip.wo.endDate)}
                    </p>
                    {tooltip.wo.estCost != null && (
                        <p className={isDark ? 'text-gray-400' : 'text-gray-500'}>Cost: {formatCost(tooltip.wo.estCost)}</p>
                    )}
                    <span className={`inline-block mt-1 px-1.5 py-0.5 rounded text-[10px] font-medium ${
                        tooltip.wo.status === 'Active' ? 'bg-blue-500/20 text-blue-400' : 'bg-emerald-500/20 text-emerald-400'
                    }`}>{tooltip.wo.status}</span>
                </div>
            )}
        </div>
    )
}

export default WorkoverSchedule
